import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  Spin,
  Alert,
  Tag,
  Progress,
  Tooltip,
  Input,
  message,
  Divider,
  Badge,
  Tabs,
  Empty,
  Statistic as AntStatistic,
} from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  FilterOutlined,
  DollarOutlined,
  FileTextOutlined,
  UserOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  getVendorInvoiceStatistics,
  exportVendorInvoiceStatistics,
  getAllPartners,
  getAllInvoicesParallel,
  getAllCreditsParallel,
} from "../Accounting/api/accounting";
import {
  fetchBrandsBySelectedClients,
  getAllClients,
} from "../../core/api/settings";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import useGlobalFilters from "../../hooks/useGlobalFilters";
import "./components/DashboardStyles.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../components/AgGridWithColumnState";

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Add inline styles for layout fixes
const vendorReportStyles = {
  container: {
    padding: "24px",
    width: "100%",
    position: "relative",
    zIndex: 1,
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
  },
  card: {
    zIndex: 2,
    position: "relative",
    marginBottom: "24px",
  },
  table: {
    zIndex: 3,
    position: "relative",
  },
};

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

/**
 * NewVendorReport - Read-only Analytics Report
 *
 * This component provides vendor performance analytics and reporting.
 * It is designed as a read-only report for data analysis, not for editing vendor data.
 *
 * Features:
 * - Vendor performance metrics and statistics
 * - Payment status tracking and analysis
 * - Export functionality for reporting
 * - View-only vendor details (no editing capabilities)
 */
const NewVendorReport = () => {
  const selectedClients = useSelector((state) => state.selectedClients);
  const { selectedBrands, brandIds } = useGlobalFilters();

  // State management
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [dateFilterType, setDateFilterType] = useState("this_month");
  const [dateRange, setDateRange] = useState(null); // Only used for custom range
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("total_amount");
  const [sortOrder, setSortOrder] = useState("descend");
  const [activeTab, setActiveTab] = useState("overview");
  const [paginationState, setPaginationState] = useState({
    current: 1,
    pageSize: 20,
  });
  const [vendorInvoices, setVendorInvoices] = useState([]);
  const [vendorCredits, setVendorCredits] = useState([]);
  const [loadingVendorDetails, setLoadingVendorDetails] = useState(false);

  // Calculate date range based on filter type
  const getDateRangeFromFilter = useCallback(
    (filterType) => {
      const today = dayjs();
      let start, end;

      switch (filterType) {
        case "this_month":
          start = today.startOf("month");
          end = today.endOf("month");
          break;
        case "last_month":
          start = today.subtract(1, "month").startOf("month");
          end = today.subtract(1, "month").endOf("month");
          break;
        case "year_to_date":
          start = today.startOf("year");
          end = today.endOf("day");
          break;
        case "last_year":
          start = today.subtract(1, "year").startOf("year");
          end = today.subtract(1, "year").endOf("year");
          break;
        case "custom":
          // Use custom dateRange if available, otherwise default to this month
          if (
            dateRange &&
            dateRange.length === 2 &&
            dateRange[0] &&
            dateRange[1]
          ) {
            start = dateRange[0];
            end = dateRange[1];
          } else {
            start = today.startOf("month");
            end = today.endOf("month");
          }
          break;
        default:
          start = today.startOf("month");
          end = today.endOf("month");
      }

      return [start, end];
    },
    [dateRange]
  );

  // Get current effective date range
  const effectiveDateRange = useMemo(() => {
    return getDateRangeFromFilter(dateFilterType);
  }, [dateFilterType, getDateRangeFromFilter]);

  // Memoize brandIds to prevent infinite loops
  const brandIdsKey = useMemo(() => brandIds.join(","), [brandIds]);
  const dateRangeKey = useMemo(
    () =>
      `${effectiveDateRange[0]?.format(
        "YYYY-MM-DD"
      )}_${effectiveDateRange[1]?.format("YYYY-MM-DD")}`,
    [effectiveDateRange]
  );

  // Fetch vendor data
  const fetchVendorData = useCallback(async () => {
    console.log("fetchVendorData called with brandIds:", brandIds);

    if (!brandIds.length) {
      console.log("No brands selected, setting vendors to empty array");
      setVendors([]);
      return;
    }

    setLoading(true);
    try {
      const filters = {};

      // Use effective date range (calculated from filter type)
      const [startDate, endDate] = effectiveDateRange;
      if (startDate && endDate) {
        filters.start_date = startDate.format("YYYY-MM-DD");
        filters.end_date = endDate.format("YYYY-MM-DD");
      }

      console.log("Calling getVendorInvoiceStatistics with:", {
        brandIds,
        filters,
      });
      const data = await getVendorInvoiceStatistics(brandIds, filters);
      console.log("Vendor statistics response:", data);
      setVendors(data || []);
    } catch (error) {
      console.error("Error fetching vendor data:", error);
      message.error("Failed to fetch vendor data");
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [brandIds, effectiveDateRange]);

  // Fetch data when brands or date filter changes
  useEffect(() => {
    if (brandIds.length > 0) {
      fetchVendorData();
    }
  }, [brandIdsKey, dateRangeKey, fetchVendorData]);

  // Fetch vendor invoices and credits when a vendor is selected
  const fetchVendorDetails = useCallback(async (vendor) => {
    if (!vendor || !vendor.vendor_id) {
      setVendorInvoices([]);
      setVendorCredits([]);
      return;
    }

    setLoadingVendorDetails(true);
    try {
      const allVendorIds = vendor._merged_vendor_ids || [vendor.vendor_id];
      const allVendorIdNums = new Set(allVendorIds.map(Number));

      const fetchPromises = allVendorIds.map((vid) => {
        const filters = {
          partner_id: vid,
          invoice_type: "bill",
        };
        if (brandIds && brandIds.length > 0) {
          filters.brand = brandIds.join(",");
        }
        return getAllInvoicesParallel(filters).catch(() => ({ results: [], count: 0 }));
      });

      const creditPromises = allVendorIds.map((vid) => {
        const creditFilters = {
          partner: vid,
          credit_type: "bill",
        };
        if (brandIds && brandIds.length > 0) {
          creditFilters.brand = brandIds.join(",");
        }
        return getAllCreditsParallel(creditFilters).catch(() => ({ results: [], count: 0 }));
      });

      const [invoiceResponses, creditResponses] = await Promise.all([
        Promise.all(fetchPromises),
        Promise.all(creditPromises),
      ]);

      const allInvoiceResults = invoiceResponses.flatMap((r) => r.results || []);
      const allCreditResults = creditResponses.flatMap((r) => r.results || []);

      const vendorInvoices = allInvoiceResults.filter((invoice) => {
        const invoicePartnerId = invoice.partner?.id || invoice.partner_id;
        return allVendorIdNums.has(Number(invoicePartnerId)) && invoice.invoice_type === "bill";
      });

      const vendorCredits = allCreditResults.filter((credit) => {
        const creditPartnerId = credit.partner?.id || credit.partner_id;
        return allVendorIdNums.has(Number(creditPartnerId)) && credit.credit_type === "bill";
      });

      console.log("Filtered vendor bills:", {
        vendorId: vendor.vendor_id,
        vendorName: vendor.vendor_name,
        filteredCount: vendorInvoices.length,
        expectedCount: vendor.total_invoices,
      });

      setVendorInvoices(vendorInvoices);
      setVendorCredits(vendorCredits);
    } catch (error) {
      console.error("Error fetching vendor details:", error);
      message.error("Failed to load vendor invoices and credits");
      setVendorInvoices([]);
      setVendorCredits([]);
    } finally {
      setLoadingVendorDetails(false);
    }
  }, [effectiveDateRange, brandIds]);

  // Fetch vendor details when selected vendor changes
  useEffect(() => {
    if (selectedVendor) {
      fetchVendorDetails(selectedVendor);
    } else {
      setVendorInvoices([]);
      setVendorCredits([]);
    }
  }, [selectedVendor, fetchVendorDetails]);

  // Merge same-name vendors across brands when multiple brands are selected
  const mergedVendors = useMemo(() => {
    if (!vendors.length) return [];
    if (brandIds.length <= 1) return vendors;

    const vendorMap = {};
    vendors.forEach((v) => {
      const key = (v.vendor_name || "").trim().toUpperCase();
      if (!vendorMap[key]) {
        vendorMap[key] = {
          ...v,
          brand_ids: Array.isArray(v.brand_ids) ? [...v.brand_ids] : v.brand_ids ? [v.brand_ids] : [],
          _merged_vendor_ids: [v.vendor_id],
        };
      } else {
        const m = vendorMap[key];
        m.total_invoices = (m.total_invoices || 0) + (v.total_invoices || 0);
        m.total_amount = (m.total_amount || 0) + (v.total_amount || 0);
        m.total_paid = (m.total_paid || 0) + (v.total_paid || 0);
        m.total_unpaid = (m.total_unpaid || 0) + (v.total_unpaid || 0);
        m.overpayment_amount = (m.overpayment_amount || 0) + (v.overpayment_amount || 0);
        m.credit_count = (m.credit_count || 0) + (v.credit_count || 0);
        m.credit_amount = (m.credit_amount || 0) + (v.credit_amount || 0);
        m.credit_allocated = (m.credit_allocated || 0) + (v.credit_allocated || 0);
        m.credit_unallocated = (m.credit_unallocated || 0) + (v.credit_unallocated || 0);
        const newBrandIds = Array.isArray(v.brand_ids) ? v.brand_ids : v.brand_ids ? [v.brand_ids] : [];
        newBrandIds.forEach((bid) => {
          if (!m.brand_ids.includes(bid)) m.brand_ids.push(bid);
        });
        m._merged_vendor_ids.push(v.vendor_id);
      }
    });
    return Object.values(vendorMap);
  }, [vendors, brandIds]);

  // Filter and sort vendors
  const processedVendors = useMemo(() => {
    let filtered = mergedVendors;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((vendor) =>
        vendor.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply payment status filter
    if (paymentStatusFilter !== "all") {
      filtered = filtered.filter((vendor) => {
        const percentPaid =
          vendor.total_amount > 0
            ? (vendor.total_paid / vendor.total_amount) * 100
            : 0;

        switch (paymentStatusFilter) {
          case "fully_paid":
            return percentPaid >= 99.99;
          case "partially_paid":
            return percentPaid > 0 && percentPaid < 99.99;
          case "unpaid":
            return percentPaid === 0;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField] || 0;
      let bValue = b[sortField] || 0;

      if (sortField === "vendor_name") {
        aValue = a.vendor_name?.toLowerCase() || "";
        bValue = b.vendor_name?.toLowerCase() || "";
        return sortOrder === "ascend"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === "ascend" ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [vendors, searchTerm, paymentStatusFilter, sortField, sortOrder]);

  // Calculate summary statistics from merged vendors
  const summaryStats = useMemo(() => {
    if (!mergedVendors.length) {
      return {
        totalVendors: 0,
        totalInvoices: 0,
        totalAmount: 0,
        totalPaid: 0,
        totalUnpaid: 0,
        totalOverpayment: 0,
        totalCredits: 0,
        creditAmount: 0,
        avgPaymentTime: 0,
      };
    }

    const stats = mergedVendors.reduce(
      (acc, vendor) => {
        acc.totalVendors++;
        acc.totalInvoices += vendor.total_invoices || 0;
        acc.totalAmount += vendor.total_amount || 0;
        acc.totalPaid += vendor.total_paid || 0;
        acc.totalUnpaid += vendor.total_unpaid || 0;
        acc.totalOverpayment += vendor.overpayment_amount || 0;
        acc.totalCredits += vendor.credit_count || 0;
        acc.creditAmount += vendor.credit_amount || 0;
        acc.creditAllocated += vendor.credit_allocated || 0;
        acc.creditUnallocated += vendor.credit_unallocated || 0;
        return acc;
      },
      {
        totalVendors: 0,
        totalInvoices: 0,
        totalAmount: 0,
        totalPaid: 0,
        totalUnpaid: 0,
        totalOverpayment: 0,
        totalCredits: 0,
        creditAmount: 0,
        creditAllocated: 0,
        creditUnallocated: 0,
      }
    );

    stats.avgPaymentPercentage =
      stats.totalAmount > 0 ? (stats.totalPaid / stats.totalAmount) * 100 : 0;

    return stats;
  }, [mergedVendors]);

  // Calculate filtered statistics (based on processedVendors after search/status filters)
  const filteredStats = useMemo(() => {
    if (!processedVendors.length) {
      return {
        totalVendors: 0,
        totalInvoices: 0,
        totalAmount: 0,
        totalPaid: 0,
        totalOutstanding: 0,
        totalOverpayment: 0,
        totalCredits: 0,
        creditAmount: 0,
        avgPaymentPercentage: 0,
      };
    }

    const stats = processedVendors.reduce(
      (acc, vendor) => {
        acc.totalVendors++;
        acc.totalInvoices += vendor.total_invoices || 0;
        acc.totalAmount += vendor.total_amount || 0;
        acc.totalPaid += vendor.total_paid || 0;
        acc.totalOverpayment += vendor.overpayment_amount || 0;
        acc.totalCredits += vendor.credit_count || 0;
        acc.creditAmount += vendor.credit_amount || 0;
        return acc;
      },
      {
        totalVendors: 0,
        totalInvoices: 0,
        totalAmount: 0,
        totalPaid: 0,
        totalOverpayment: 0,
        totalCredits: 0,
        creditAmount: 0,
      }
    );

    stats.totalOutstanding = Math.max(stats.totalAmount - stats.totalPaid, 0);
    stats.avgPaymentPercentage =
      stats.totalAmount > 0 ? (stats.totalPaid / stats.totalAmount) * 100 : 0;

    return stats;
  }, [processedVendors]);

  // Determine if filters are active (to show/hide filtered dashboard)
  const isFiltered = searchTerm || paymentStatusFilter !== "all";

  // Handle export
  const handleExport = async () => {
    if (!brandIds.length) {
      message.warning("Please select at least one brand from global filter");
      return;
    }

    setExportLoading(true);
    try {
      const [startDate, endDate] = effectiveDateRange;
      const filters = {
        start_date: startDate?.format("YYYY-MM-DD"),
        end_date: endDate?.format("YYYY-MM-DD"),
      };

      const response = await exportVendorInvoiceStatistics(brandIds, filters);

      // Create download link with date range in filename
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const periodStart = startDate?.format("YYYY-MM-DD") || "start";
      const periodEnd = endDate?.format("YYYY-MM-DD") || "end";
      link.setAttribute(
        "download",
        `vendor_report_${periodStart}_to_${periodEnd}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Export completed successfully!");
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };

  // Table columns
  const columns = [
    {
      title: "Vendor",
      dataIndex: "vendor_name",
      key: "vendor_name",
      width: 200,
      fixed: "left",
      sorter: true,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{text || "Unknown Vendor"}</div>
          {record._merged_vendor_ids?.length > 1 ? (
            <div style={{ fontSize: "12px", color: "#999" }}>
              {record._merged_vendor_ids.length} brands merged
            </div>
          ) : (
            <div style={{ fontSize: "12px", color: "#666" }}>
              ID: {record.vendor_id}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Brand",
      dataIndex: "brand_ids",
      key: "brand",
      width: 120,
      render: (vendorBrandIds) => {
        const brandNames = Array.isArray(vendorBrandIds)
          ? vendorBrandIds
              .map((id) => selectedBrands.find((b) => b.id === id)?.brand_name)
              .filter(Boolean)
          : [
              selectedBrands.find((b) => b.id === vendorBrandIds)?.brand_name,
            ].filter(Boolean);

        return (
          <div>
            {brandNames.map((name, index) => (
              <Tag key={index} color="blue" size="small">
                {name}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Invoices",
      dataIndex: "total_invoices",
      key: "total_invoices",
      width: 100,
      align: "center",
      sorter: true,
      render: (value) => (
        <Badge
          count={value || 0}
          style={{ backgroundColor: "#52c41a" }}
          overflowCount={999999}
        />
      ),
    },
    {
      title: "Credits",
      dataIndex: "credit_count",
      key: "credit_count",
      width: 100,
      align: "center",
      sorter: true,
      render: (value) => (
        <Badge count={value || 0} style={{ backgroundColor: "#faad14" }} />
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      width: 150,
      align: "right",
      sorter: true,
      render: (value) => (
        <Text strong style={{ fontSize: "14px", fontVariantNumeric: "tabular-nums" }}>
          ${(value || 0).toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
        </Text>
      ),
    },
    {
      title: "Paid",
      dataIndex: "total_paid",
      key: "total_paid",
      width: 150,
      align: "right",
      sorter: true,
      render: (value) => (
        <Text style={{ color: "#52c41a", fontSize: "14px", fontVariantNumeric: "tabular-nums" }}>
          ${(value || 0).toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
        </Text>
      ),
    },
    {
      title: "Unpaid",
      key: "total_unpaid",
      width: 150,
      align: "right",
      sorter: true,
      sortDirections: ["descend", "ascend"],
      render: (_, record) => {
        const outstanding = Math.max((record.total_amount || 0) - (record.total_paid || 0), 0);
        return (
          <Text
            style={{
              color: outstanding > 0 ? "#ff4d4f" : "#52c41a",
              fontSize: "14px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            ${outstanding.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
          </Text>
        );
      },
    },
    {
      title: "Payment Status",
      key: "payment_status",
      width: 140,
      render: (_, record) => {
        const percentPaid =
          record.total_amount > 0
            ? (record.total_paid / record.total_amount) * 100
            : 0;

        let status, color, icon;
        if (percentPaid >= 99.99) {
          status = "Fully Paid";
          color = "success";
          icon = <CheckCircleOutlined />;
        } else if (percentPaid > 0) {
          status = "Partially Paid";
          color = "warning";
          icon = <ClockCircleOutlined />;
        } else {
          status = "Unpaid";
          color = "error";
          icon = <ExclamationCircleOutlined />;
        }

        return (
          <div>
            <Tag color={color} icon={icon}>
              {status}
            </Tag>
            <div style={{ marginTop: 4 }}>
              <Progress
                percent={Math.round(percentPaid)}
                size="small"
                strokeColor={
                  percentPaid >= 99.99
                    ? "#52c41a"
                    : percentPaid > 0
                    ? "#faad14"
                    : "#ff4d4f"
                }
                showInfo={false}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: "Credit Allocated",
      dataIndex: "credit_allocated",
      key: "credit_allocated",
      width: 150,
      align: "right",
      sorter: true,
      render: (value) => (
        <Text style={{ color: "#52c41a", fontSize: "14px", fontVariantNumeric: "tabular-nums" }}>
          ${(value || 0).toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
        </Text>
      ),
    },
    {
      title: "Credit Unallocated",
      dataIndex: "credit_unallocated",
      key: "credit_unallocated",
      width: 150,
      align: "right",
      sorter: true,
      render: (value) => (
        <Text style={{ color: "#ff4d4f", fontSize: "14px", fontVariantNumeric: "tabular-nums" }}>
          ${(value || 0).toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
        </Text>
      ),
    },
    {
      title: "Credit Total",
      dataIndex: "credit_amount",
      key: "credit_amount",
      width: 150,
      align: "right",
      sorter: true,
      render: (value) => (
        <Text strong style={{ fontSize: "14px", fontVariantNumeric: "tabular-nums" }}>
          ${(value || 0).toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
        </Text>
      ),
    },
    {
      title: "Overpayment",
      dataIndex: "overpayment_amount",
      key: "overpayment_amount",
      width: 150,
      align: "right",
      sorter: true,
      render: (value) => {
        const amount = value || 0;
        return (
          <Text style={{ color: amount > 0 ? "#1890ff" : "#d9d9d9", fontSize: "14px", fontVariantNumeric: "tabular-nums" }}>
            ${amount.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
          </Text>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="View Vendor Details">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setSelectedVendor(record)}
            style={{ color: "#1890ff" }}
          />
        </Tooltip>
      ),
    },
  ];

  // Handle table change
  const handleTableChange = (pagination, filters, sorter) => {
    // Update pagination state
    if (pagination) {
      setPaginationState({
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 20,
      });
    }
    // Update sorting
    if (sorter && sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    }
  };

  // Column definitions for vendor invoices (bills)
  const invoiceColumns = useMemo(
    () => [
      {
        headerName: "Bill Number",
        field: "number",
        sortable: true,
        filter: true,
        width: 180,
        resizable: true,
      },
      {
        headerName: "Issue Date",
        field: "issue_date",
        sortable: true,
        filter: true,
        width: 130,
        resizable: true,
        cellRenderer: (params) => {
          if (!params.value) return "N/A";
          return dayjs(params.value).format("MMM DD, YYYY");
        },
      },
      {
        headerName: "Due Date",
        field: "due_date",
        sortable: true,
        filter: true,
        width: 130,
        resizable: true,
        valueGetter: (params) => {
          // Try multiple possible field names
          return (
            params.data?.due_date ||
            params.data?.originalData?.due_date ||
            null
          );
        },
        cellRenderer: (params) => {
          // Check both the field value and originalData
          const dueDate =
            params.value ||
            params.data?.due_date ||
            params.data?.originalData?.due_date;
          
          if (!dueDate) return "N/A";
          
          // Handle both string and date object formats
          const date = dayjs(dueDate);
          if (!date.isValid()) return "N/A";
          
          return date.format("MMM DD, YYYY");
        },
      },
      {
        headerName: "Type",
        field: "invoice_type",
        sortable: true,
        filter: true,
        width: 120,
        resizable: true,
        cellRenderer: (params) => {
          const type = params.value || "bill";
          return (
            <Tag color={type === "invoice" ? "green" : "blue"}>
              {type === "invoice" ? "Invoice" : "Bill"}
            </Tag>
          );
        },
      },
      {
        headerName: "Total Amount",
        field: "total_amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        resizable: true,
        cellRenderer: (params) => {
          const value = params.value || 0;
          return `$${value.toLocaleString("en-US", {
            minimumFractionDigits: getDecimalPrecision(),
            maximumFractionDigits: getDecimalPrecision(),
          })}`;
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Paid Amount",
        field: "paid_amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        resizable: true,
        valueGetter: (params) => {
          return params.data?.paid_amount || params.data?.allocated_amount || 0;
        },
        cellRenderer: (params) => {
          const value = params.data?.paid_amount || params.data?.allocated_amount || 0;
          return (
            <span style={{ color: "#52c41a" }}>
              ${value.toLocaleString("en-US", {
                minimumFractionDigits: getDecimalPrecision(),
                maximumFractionDigits: getDecimalPrecision(),
              })}
            </span>
          );
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Unpaid Amount",
        field: "unpaid_amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        resizable: true,
        valueGetter: (params) => {
          const total = params.data?.total_amount || 0;
          const paid = params.data?.paid_amount || params.data?.allocated_amount || 0;
          return total - paid;
        },
        cellRenderer: (params) => {
          const total = params.data?.total_amount || 0;
          const paid = params.data?.paid_amount || params.data?.allocated_amount || 0;
          const unpaid = total - paid;
          return (
            <span style={{ color: unpaid > 0 ? "#ff4d4f" : "#52c41a" }}>
              ${unpaid.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Status",
        field: "status",
        sortable: true,
        filter: true,
        width: 120,
        resizable: true,
        cellRenderer: (params) => {
          const status = params.value || "draft";
          const statusConfig = {
            paid: { color: "green", text: "Paid" },
            pending: { color: "orange", text: "Pending" },
            overdue: { color: "red", text: "Overdue" },
            draft: { color: "default", text: "Draft" },
          };
          const config = statusConfig[status.toLowerCase()] || {
            color: "default",
            text: status,
          };
          return <Tag color={config.color}>{config.text}</Tag>;
        },
      },
    ],
    []
  );

  // Column definitions for vendor credits
  const creditColumns = useMemo(
    () => [
      {
        headerName: "Credit Number",
        field: "number",
        sortable: true,
        filter: true,
        width: 180,
        resizable: true,
      },
      {
        headerName: "Issue Date",
        field: "issue_date",
        sortable: true,
        filter: true,
        width: 130,
        resizable: true,
        cellRenderer: (params) => {
          if (!params.value) return "N/A";
          return dayjs(params.value).format("MMM DD, YYYY");
        },
      },
      {
        headerName: "Type",
        field: "credit_type",
        sortable: true,
        filter: true,
        width: 120,
        resizable: true,
        cellRenderer: (params) => {
          const type = params.value || "bill";
          return (
            <Tag color={type === "invoice" ? "green" : "blue"}>
              {type === "invoice" ? "Invoice Credit" : "Bill Credit"}
            </Tag>
          );
        },
      },
      {
        headerName: "Total Amount",
        field: "total_amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        resizable: true,
        cellRenderer: (params) => {
          const value = params.value || 0;
          return `$${value.toLocaleString("en-US", {
            minimumFractionDigits: getDecimalPrecision(),
            maximumFractionDigits: getDecimalPrecision(),
          })}`;
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Allocated Amount",
        field: "allocated_amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        resizable: true,
        cellRenderer: (params) => {
          const value = params.value || 0;
          return (
            <span style={{ color: "#52c41a" }}>
              ${value.toLocaleString("en-US", {
                minimumFractionDigits: getDecimalPrecision(),
                maximumFractionDigits: getDecimalPrecision(),
              })}
            </span>
          );
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Unallocated Amount",
        field: "unallocated_amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 170,
        resizable: true,
        cellRenderer: (params) => {
          const total = params.data?.total_amount || 0;
          const allocated = params.data?.allocated_amount || 0;
          const unallocated = total - allocated;
          return (
            <span style={{ color: unallocated > 0 ? "#ff4d4f" : "#52c41a" }}>
              ${unallocated.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        },
        cellStyle: { textAlign: "right" },
      },
      {
        headerName: "Status",
        field: "status",
        sortable: true,
        filter: true,
        width: 120,
        resizable: true,
        cellRenderer: (params) => {
          const total = params.data?.total_amount || 0;
          const allocated = params.data?.allocated_amount || 0;
          const percentAllocated = total > 0 ? (allocated / total) * 100 : 0;
          let status, color;
          if (percentAllocated >= 99.99) {
            status = "Fully Allocated";
            color = "green";
          } else if (percentAllocated > 0) {
            status = "Partially Allocated";
            color = "orange";
          } else {
            status = "Unallocated";
            color = "default";
          }
          return <Tag color={color}>{status}</Tag>;
        },
      },
    ],
    []
  );

  // Default column definition for AG Grid
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
    }),
    []
  );

  // Summary cards data
  const summaryCards = [
    {
      title: "Total Vendors",
      value: summaryStats.totalVendors,
      icon: <UserOutlined />,
      color: "#1890ff",
    },
    {
      title: "Total Invoices",
      value: summaryStats.totalInvoices,
      icon: <FileTextOutlined />,
      color: "#52c41a",
    },
    {
      title: "Total Amount",
      value: summaryStats.totalAmount,
      icon: <DollarOutlined />,
      color: "#722ed1",
      prefix: "$",
      precision: getDecimalPrecision(),
    },
    {
      title: "Total Paid",
      value: summaryStats.totalPaid,
      icon: <CheckCircleOutlined />,
      color: "#52c41a",
      prefix: "$",
      precision: getDecimalPrecision(),
    },
    {
      title: "Total Unpaid",
      value: Math.max(summaryStats.totalAmount - summaryStats.totalPaid, 0),
      icon: <ExclamationCircleOutlined />,
      color: "#ff4d4f",
      prefix: "$",
      precision: getDecimalPrecision(),
    },
    {
      title: "Avg Payment %",
      value: summaryStats.avgPaymentPercentage,
      icon: <CreditCardOutlined />,
      color: "#faad14",
      suffix: "%",
      precision: 1,
    },
  ];

  return (
    <div className="page-wrapper">
      <div
        className="vendor-report-container"
        style={vendorReportStyles.container}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <Title level={2} style={{ margin: 0 }}>
            Vendor Report
          </Title>
          <Text type="secondary">
            Comprehensive vendor performance and payment analysis
          </Text>
        </div>

        {/* Filters */}
        <Card
          title={
            <Space>
              <FilterOutlined />
              Filters
            </Space>
          }
          style={vendorReportStyles.card}
          size="small"
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <Text strong>Date Range</Text>
                <Select
                  style={{ width: "100%" }}
                  value={dateFilterType}
                  onChange={(value) => {
                    setDateFilterType(value);
                    // Reset custom date range when switching away from custom
                    if (value !== "custom") {
                      setDateRange(null);
                    }
                  }}
                >
                  <Option value="this_month">This Month</Option>
                  <Option value="last_month">Last Month</Option>
                  <Option value="year_to_date">Year to Date</Option>
                  <Option value="last_year">Last Year</Option>
                  <Option value="custom">Custom Range</Option>
                </Select>
                {dateFilterType === "custom" && (
                  <RangePicker
                    style={{ width: "100%", marginTop: 8 }}
                    value={dateRange || effectiveDateRange}
                    onChange={(dates) => {
                      // Handle null/cleared dates gracefully
                      if (dates && dates.length === 2 && dates[0] && dates[1]) {
                        setDateRange(dates);
                      } else {
                        // If cleared, reset to this month
                        setDateRange(null);
                        setDateFilterType("this_month");
                      }
                    }}
                    format="MMM DD, YYYY"
                    allowClear={false}
                  />
                )}
                {dateFilterType !== "custom" && (
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    {effectiveDateRange[0]?.format("MMM DD, YYYY")} -{" "}
                    {effectiveDateRange[1]?.format("MMM DD, YYYY")}
                  </Text>
                )}
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <Text strong>Filter by Vendor</Text>
                <Search
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <Text strong>Payment Status</Text>
                <Select
                  style={{ width: "100%" }}
                  value={paymentStatusFilter}
                  onChange={setPaymentStatusFilter}
                >
                  <Option value="all">All Status</Option>
                  <Option value="fully_paid">Fully Paid</Option>
                  <Option value="partially_paid">Partially Paid</Option>
                  <Option value="unpaid">Unpaid</Option>
                </Select>
              </Space>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Space>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={fetchVendorData}
                  loading={loading}
                >
                  Apply Filters
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  loading={exportLoading}
                  disabled={!brandIds.length}
                >
                  Export Excel
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Summary Statistics */}
        <Row gutter={[12, 12]} style={{ marginBottom: "16px" }}>
          {summaryCards.map((card, index) => (
            <Col xs={12} sm={8} md={4} key={index}>
              <Card
                size="small"
                style={{ zIndex: 2, position: "relative" }}
                bodyStyle={{ padding: "12px 16px" }}
              >
                <Statistic
                  title={<span style={{ fontSize: "12px" }}>{card.title}</span>}
                  value={card.value}
                  prefix={card.prefix}
                  suffix={card.suffix}
                  precision={card.precision}
                  valueStyle={{ color: card.color, fontVariantNumeric: "tabular-nums", fontSize: "18px" }}
                  formatter={(value) => {
                    if (card.prefix === "$") {
                      return value.toLocaleString("en-US", {
                        minimumFractionDigits: getDecimalPrecision(),
                        maximumFractionDigits: getDecimalPrecision(),
                      });
                    }
                    return value.toLocaleString();
                  }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filtered Overview Dashboard - shown when search or status filters are active */}
        {isFiltered && (
          <Card
            title={
              <Space>
                <FilterOutlined />
                Filtered Overview
                <Text type="secondary" style={{ fontSize: "13px", fontWeight: 400 }}>
                  — Totals based on current filter selections
                  {searchTerm && <Tag color="blue" style={{ marginLeft: 8 }}>Vendor: &ldquo;{searchTerm}&rdquo;</Tag>}
                  {paymentStatusFilter !== "all" && (
                    <Tag color="purple" style={{ marginLeft: 4 }}>
                      Status: {paymentStatusFilter === "fully_paid" ? "Fully Paid" : paymentStatusFilter === "partially_paid" ? "Partially Paid" : "Unpaid"}
                    </Tag>
                  )}
                </Text>
              </Space>
            }
            style={{ ...vendorReportStyles.card, borderLeft: "4px solid #1890ff" }}
            size="small"
          >
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Vendors"
                  value={filteredStats.totalVendors}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Invoices"
                  value={filteredStats.totalInvoices}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Total Amount"
                  value={filteredStats.totalAmount}
                  prefix="$"
                  precision={getDecimalPrecision()}
                  valueStyle={{ color: "#722ed1", fontVariantNumeric: "tabular-nums" }}
                  formatter={(value) =>
                    value.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })
                  }
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Total Paid"
                  value={filteredStats.totalPaid}
                  prefix="$"
                  precision={getDecimalPrecision()}
                  valueStyle={{ color: "#52c41a", fontVariantNumeric: "tabular-nums" }}
                  formatter={(value) =>
                    value.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })
                  }
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Outstanding"
                  value={filteredStats.totalOutstanding}
                  prefix="$"
                  precision={getDecimalPrecision()}
                  valueStyle={{ color: "#ff4d4f", fontVariantNumeric: "tabular-nums" }}
                  formatter={(value) =>
                    value.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })
                  }
                />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic
                  title="Avg Payment %"
                  value={filteredStats.avgPaymentPercentage}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: "#faad14", fontVariantNumeric: "tabular-nums" }}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* Main Content */}
        <Card style={vendorReportStyles.card}>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="Vendor Overview" key="overview">
              <Spin spinning={loading}>
                {processedVendors.length === 0 ? (
                  <Empty
                    description="No vendor data found"
                    style={{ padding: "60px 0" }}
                  />
                ) : (
                  <Table
                    columns={columns}
                    dataSource={processedVendors}
                    rowKey={(record) => record._merged_vendor_ids ? record._merged_vendor_ids.join("-") : record.vendor_id}
                    scroll={{ x: 2000, y: 600 }}
                    pagination={false}
                    onChange={handleTableChange}
                    size="small"
                    style={vendorReportStyles.table}
                    summary={(pageData) => {
                      // Use all processedVendors for totals (not just current page)
                      const totals = processedVendors.reduce(
                        (acc, vendor) => {
                          acc.invoices += vendor.total_invoices || 0;
                          acc.credits += vendor.credit_count || 0;
                          acc.totalAmount += vendor.total_amount || 0;
                          acc.totalPaid += vendor.total_paid || 0;
                          acc.overpayment += vendor.overpayment_amount || 0;
                          acc.creditAmount += vendor.credit_amount || 0;
                          acc.creditAllocated += vendor.credit_allocated || 0;
                          acc.creditUnallocated +=
                            vendor.credit_unallocated || 0;
                          return acc;
                        },
                        {
                          invoices: 0,
                          credits: 0,
                          totalAmount: 0,
                          totalPaid: 0,
                          overpayment: 0,
                          creditAmount: 0,
                          creditAllocated: 0,
                          creditUnallocated: 0,
                        }
                      );
                      // Outstanding balance only (no overpayment offset)
                      const totalOutstanding = Math.max(totals.totalAmount - totals.totalPaid, 0);

                      return (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>
                              <Text strong>
                                Total ({processedVendors.length} vendors)
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} />
                            <Table.Summary.Cell index={2} align="center">
                              <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>
                                {totals.invoices.toLocaleString()}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="center">
                              <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>
                                {totals.credits.toLocaleString()}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} align="right">
                              <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>
                                ${totals.totalAmount.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5} align="right">
                              <Text strong style={{ color: "#52c41a", fontVariantNumeric: "tabular-nums" }}>
                                ${totals.totalPaid.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={6} align="right">
                              <Text strong style={{ color: "#ff4d4f", fontVariantNumeric: "tabular-nums" }}>
                                ${totalOutstanding.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={7} />
                            <Table.Summary.Cell index={8} align="right">
                              <Text strong style={{ color: "#52c41a", fontVariantNumeric: "tabular-nums" }}>
                                ${totals.creditAllocated.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={9} align="right">
                              <Text strong style={{ color: "#ff4d4f", fontVariantNumeric: "tabular-nums" }}>
                                ${totals.creditUnallocated.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={10} align="right">
                              <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>
                                ${totals.creditAmount.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={11} align="right">
                              <Text strong style={{ color: "#1890ff", fontVariantNumeric: "tabular-nums" }}>
                                ${totals.overpayment.toLocaleString("en-US", { minimumFractionDigits: getDecimalPrecision(), maximumFractionDigits: getDecimalPrecision() })}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={12} />
                          </Table.Summary.Row>
                        </Table.Summary>
                      );
                    }}
                  />
                )}
              </Spin>

              {/* Vendor Analytics - Shows when a vendor is selected */}
              {selectedVendor && (
                <div style={{ marginTop: "24px" }}>
                  <Card
                    title={`Vendor Analytics: ${selectedVendor.vendor_name}`}
                    size="small"
                  >
                    <Row gutter={[16, 16]}>
                      <Col span={6}>
                        <Statistic
                          title="Total Bills"
                          value={selectedVendor.total_invoices || 0}
                          prefix={<FileTextOutlined />}
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic
                          title="Total Amount"
                          value={selectedVendor.total_amount || 0}
                          prefix="$"
                          precision={getDecimalPrecision()}
                          valueStyle={{ color: "#1890ff" }}
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic
                          title="Paid Amount"
                          value={selectedVendor.total_paid || 0}
                          prefix="$"
                          precision={getDecimalPrecision()}
                          valueStyle={{ color: "#52c41a" }}
                        />
                      </Col>
                      <Col span={6}>
                        <Statistic
                          title="Unpaid Amount"
                          value={selectedVendor.total_unpaid || 0}
                          prefix="$"
                          precision={getDecimalPrecision()}
                          valueStyle={{ color: "#ff4d4f" }}
                        />
                      </Col>
                    </Row>
                    <div style={{ marginTop: "16px", textAlign: "right" }}>
                      <Button
                        type="link"
                        onClick={() => {
                          setSelectedVendor(null);
                          setVendorInvoices([]);
                          setVendorCredits([]);
                        }}
                        icon={<EyeOutlined />}
                      >
                        Close Analytics
                      </Button>
                    </div>
                  </Card>

                  {/* Vendor Bills AG Grid */}
                  <Card
                    title="Bills"
                    size="small"
                    style={{ marginTop: "16px" }}
                    extra={
                      <Badge
                        count={vendorInvoices.length}
                        style={{ backgroundColor: "#52c41a" }}
                      />
                    }
                  >
                    <Spin spinning={loadingVendorDetails}>
                      <div
                        className="ag-theme-alpine"
                        style={{ height: 400, width: "100%" }}
                      >
                        <AgGridWithColumnState
                          tableId="vendor-report-invoices"
                          rowData={vendorInvoices}
                          columnDefs={invoiceColumns}
                          defaultColDef={defaultColDef}
                          loading={loadingVendorDetails}
                          pagination={true}
                          paginationPageSize={50}
                          paginationPageSizeSelector={[25, 50, 100, 200]}
                          enableRangeSelection={true}
                          enableCellTextSelection={true}
                          suppressMovableColumns={false}
                          domLayout="normal"
                        />
                      </div>
                    </Spin>
                  </Card>

                  {/* Vendor Credits AG Grid */}
                  <Card
                    title="Credit Notes"
                    size="small"
                    style={{ marginTop: "16px" }}
                    extra={
                      <Badge
                        count={vendorCredits.length}
                        style={{ backgroundColor: "#faad14" }}
                      />
                    }
                  >
                    <Spin spinning={loadingVendorDetails}>
                      <div
                        className="ag-theme-alpine"
                        style={{ height: 400, width: "100%" }}
                      >
                        <AgGridWithColumnState
                          tableId="vendor-report-credits"
                          rowData={vendorCredits}
                          columnDefs={creditColumns}
                          defaultColDef={defaultColDef}
                          loading={loadingVendorDetails}
                          pagination={true}
                          paginationPageSize={50}
                          paginationPageSizeSelector={[25, 50, 100, 200]}
                          enableRangeSelection={true}
                          enableCellTextSelection={true}
                          suppressMovableColumns={false}
                          domLayout="normal"
                        />
                      </div>
                    </Spin>
                  </Card>
                </div>
              )}
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default NewVendorReport;
